import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api, setSessionToken } from '../lib/api.js';
import { getOrgSlug } from '../lib/orgSlug.js';

const AuthContext = createContext(null);

const REFRESH_INTERVAL_MS = 13 * 60 * 1000; // 13 minutes — refresh before the 15-min TTL

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    loading: true,
    error: null,
    errorType: null, // 'not_provisioned' | 'auth_failed' | 'admin_console_required' | 'org_mismatch' | 'error'
  });
  const refreshTimer = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const session = await api.post('/auth/session/refresh');
      setSessionToken(session.session_token);

      const me = await api.get('/me');
      const user = me.data || me;

      setState({ user, loading: false, error: null, errorType: null });

      clearRefreshTimer();
      refreshTimer.current = setTimeout(refreshSession, REFRESH_INTERVAL_MS);
    } catch {
      // Refresh failed — clear token; the next request will trigger re-init.
      setSessionToken(null);
      setState({
        user: null,
        loading: false,
        error: 'Session expired',
        errorType: 'error',
      });
    }
  }, [clearRefreshTimer]);

  const initSession = useCallback(async () => {
    try {
      // 1. Exchange CF Access JWT for a Verso session JWT.
      // Include org_slug if on a subdomain (e.g., lyonscomm.verso-cloud.io).
      const orgSlug = getOrgSlug();
      const session = await api.post('/auth/session', orgSlug ? { org_slug: orgSlug } : undefined);
      setSessionToken(session.session_token);

      // 2. Fetch full user context.
      const me = await api.get('/me');
      const user = me.data || me;

      setState({ user, loading: false, error: null, errorType: null });

      // 3. Schedule token refresh 2 minutes before the 15-min TTL expires.
      clearRefreshTimer();
      refreshTimer.current = setTimeout(refreshSession, REFRESH_INTERVAL_MS);
    } catch (err) {
      setSessionToken(null);

      if (err.status === 403 && err.body?.error === 'admin_console_required') {
        setState({
          user: null,
          loading: false,
          error: err.body?.message || 'Please use the admin console at admin.verso-cloud.io',
          errorType: 'admin_console_required',
        });
      } else if (err.status === 403 && err.body?.error === 'org_mismatch') {
        setState({
          user: null,
          loading: false,
          error: err.body?.message || 'You do not have access to this organization',
          errorType: 'org_mismatch',
        });
      } else if (err.status === 403) {
        setState({
          user: null,
          loading: false,
          error: err.message || 'Account not provisioned',
          errorType: 'not_provisioned',
        });
      } else if (err.status === 401) {
        // Auth failed — reload to trigger CF Access at the edge. On Pages-
        // hosted subdomains, /cdn-cgi/access/login may not be directly
        // reachable, so a hard reload is the safest recovery.
        setState({
          user: null,
          loading: false,
          error: 'Authentication required — redirecting to login...',
          errorType: 'auth_failed',
        });
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setState({
          user: null,
          loading: false,
          error: err.message || 'Unable to connect to Verso',
          errorType: 'error',
        });
      }
    }
  }, [clearRefreshTimer, refreshSession]);

  // Set the impersonation token and hydrate /me without re-initializing the
  // session. Used by the handoff flow so we don't wipe the impersonation
  // token by racing initSession(). Persisted to sessionStorage so a page
  // refresh within the same tab survives (sessionStorage clears on tab close).
  const startImpersonation = useCallback(async (impersonationToken) => {
    setSessionToken(impersonationToken);
    try {
      sessionStorage.setItem('verso_impersonation_token', impersonationToken);
      const me = await api.get('/me');
      const user = me.data || me;
      setState({ user, loading: false, error: null, errorType: null });
      clearRefreshTimer();
      // No refresh timer — impersonation tokens have 1-hour TTL and are not
      // refreshable. The admin must re-open the org view from the admin console.
    } catch (err) {
      setSessionToken(null);
      sessionStorage.removeItem('verso_impersonation_token');
      if (err.status === 401) {
        // Expired/invalid — fall back to normal verso_admin session.
        initSession();
      } else {
        setState({ user: null, loading: false, error: 'Impersonation failed', errorType: 'error' });
      }
    }
  }, [clearRefreshTimer, initSession]);

  const endImpersonation = useCallback(async () => {
    // Tell the org worker to mark the impersonation_sessions row ended, THEN
    // drop the local token. Without this the admin console's next handoff
    // sees the row as still active and refuses to start a new session for
    // the same admin. Best-effort — if the endpoint fails (network, expired
    // token) we still drop the local token so the UI recovers.
    try {
      await api.post('/auth/end-impersonation');
    } catch {
      // swallow — server-side mark is best-effort; the immutable audit row
      // survives even if this call didn't get through
    }
    setSessionToken(null);
    sessionStorage.removeItem('verso_impersonation_token');
    await initSession();
  }, [initSession]);

  useEffect(() => {
    // Skip auto-init on /handoff — that page calls startImpersonation() with
    // the one-time KV token. Running initSession() here would race and wipe
    // the token before Handoff.jsx gets a chance to use it.
    if (window.location.pathname === '/handoff') return;

    // Restore an impersonation session from sessionStorage (survives page
    // refresh within the tab). validateSessionJwt will reject if expired.
    const storedToken = sessionStorage.getItem('verso_impersonation_token');
    if (storedToken) {
      startImpersonation(storedToken);
      return () => clearRefreshTimer();
    }

    initSession();
    return () => clearRefreshTimer();
  }, [initSession, clearRefreshTimer, startImpersonation]);

  const imp = state.user?.impersonating || null;

  const value = useMemo(() => ({
    user: state.user,
    loading: state.loading,
    error: state.error,
    errorType: state.errorType,
    isAuthenticated: !!state.user,
    // Under impersonation, the effective role is org_admin (verso_admin
    // acting AS an org_admin in the target org). Non-impersonation:
    // whatever role the user actually has.
    role: imp ? 'org_admin' : (state.user?.role || null),
    orgId: imp ? imp.org_id : (state.user?.org_id || null),
    orgName: imp ? imp.org_name : (state.user?.org_name || null),
    impersonating: imp,
    refetch: initSession,
    startImpersonation,
    endImpersonation,
  }), [state, imp, initSession, startImpersonation, endImpersonation]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
